import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Truck, Package, Weight, MapPin, Plus, ChevronRight } from "lucide-react";
import { toast } from "sonner";


function TruckStatusBadge({ status }) {
  const map = {
    Planning:   "bg-amber-50 text-amber-700 border-amber-200",
    Loaded:     "bg-blue-50 text-blue-700 border-blue-200",
    Dispatched: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return <span className={`text-[11px] px-2 py-0.5 rounded-md border ${map[status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>{status}</span>;
}

function num(n) { return Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }); }


export default function Dispatch() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canAct = ["super_admin", "admin", "dispatch", "operations"].includes(user?.role);

  const [tab, setTab] = useState("unassigned");
  const [orders, setOrders] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    truck_label: "", vehicle_number: "", plan_date: new Date().toISOString().slice(0, 10),
    driver_name: "", driver_phone: "", transporter: "", route: "",
  });

  async function reloadOrders() {
    const { data } = await api.get("/orders", { params: { status: "Production Completed" } });
    setOrders(data || []);
  }
  async function reloadTrucks() {
    const { data } = await api.get("/trucks");
    setTrucks(data || []);
  }

  useEffect(() => {
    if (tab === "unassigned") reloadOrders();
    if (tab === "trucks") reloadTrucks();
    setSelected(new Set());
  }, [tab]);

  const totals = useMemo(() => {
    const sel = orders.filter((o) => selected.has(o.id));
    return {
      count: sel.length,
      boxes: sel.reduce((s, o) => s + Number(o.total_dispatch_quantity || o.total_quantity || 0), 0),
      weight: sel.reduce((s, o) => s + Number(o.total_weight || 0), 0),
    };
  }, [selected, orders]);

  function toggle(id) {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  }
  function toggleAll() {
    setSelected(selected.size === orders.length ? new Set() : new Set(orders.map((o) => o.id)));
  }

  async function createTruckAndAssign() {
    if (!form.vehicle_number.trim()) return toast.error("Vehicle number is required");
    if (selected.size === 0) return toast.error("Select at least one order");
    setBusy(true);
    try {
      const { data: truck } = await api.post("/trucks", form);
      await api.post(`/trucks/${truck.id}/assign-order`, { order_ids: Array.from(selected) });
      toast.success(`Truck ${truck.truck_label} created with ${selected.size} order(s)`);
      setCreateOpen(false);
      setSelected(new Set());
      setForm({ truck_label: "", vehicle_number: "", plan_date: new Date().toISOString().slice(0, 10),
                driver_name: "", driver_phone: "", transporter: "", route: "" });
      // Switch to trucks tab to show the new one
      setTab("trucks");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create truck");
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-6" data-testid="dispatch-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-[28px] font-bold text-slate-900 tracking-tight">Dispatch · Truck Planning</h1>
          <p className="text-sm text-slate-500 mt-1">Create trucks, assign Production-Completed PFIs, and dispatch them with a single click.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="unassigned" data-testid="disp-tab-unassigned">Unassigned Orders</TabsTrigger>
          <TabsTrigger value="trucks" data-testid="disp-tab-trucks">Trucks</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "unassigned" && (
        <UnassignedView
          orders={orders}
          selected={selected}
          totals={totals}
          canAct={canAct}
          onToggle={toggle}
          onToggleAll={toggleAll}
          onCreateTruck={() => setCreateOpen(true)}
          onOpenOrder={(id) => nav(`/orders/${id}`)}
        />
      )}

      {tab === "trucks" && (
        <TrucksView trucks={trucks} onOpen={(id) => nav(`/dispatch/trucks/${id}`)} canAct={canAct} onCreate={() => { setSelected(new Set()); setCreateOpen(true); setTab("unassigned"); }} />
      )}

      {/* Create Truck Modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-truck-modal" className="max-w-2xl">
          <DialogHeader><DialogTitle>Create Truck & Assign {selected.size} Order(s)</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <div><Label>Vehicle Number *</Label><Input data-testid="truck-form-vehicle" value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} placeholder="AP07XX9999" /></div>
            <div><Label>Truck Label</Label><Input data-testid="truck-form-label" value={form.truck_label} onChange={(e) => setForm({ ...form, truck_label: e.target.value })} placeholder="optional" /></div>
            <div><Label>Plan Date</Label><Input data-testid="truck-form-plan-date" type="date" value={form.plan_date} onChange={(e) => setForm({ ...form, plan_date: e.target.value })} /></div>
            <div><Label>Driver Name</Label><Input data-testid="truck-form-driver" value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} /></div>
            <div><Label>Driver Phone</Label><Input data-testid="truck-form-driver-phone" value={form.driver_phone} onChange={(e) => setForm({ ...form, driver_phone: e.target.value })} /></div>
            <div><Label>Transporter</Label><Input data-testid="truck-form-transporter" value={form.transporter} onChange={(e) => setForm({ ...form, transporter: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Route</Label><Input data-testid="truck-form-route" value={form.route} onChange={(e) => setForm({ ...form, route: e.target.value })} placeholder="Vizag → Hyderabad" /></div>
          </div>
          {selected.size > 0 && (
            <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-md text-sm" data-testid="create-truck-summary">
              <div className="font-semibold text-slate-700 mb-1">Selected Orders</div>
              <div className="text-slate-600">{selected.size} PFIs · <b>{num(totals.boxes)}</b> boxes · <b>{num(totals.weight)}</b> kg</div>
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createTruckAndAssign} disabled={busy || !canAct} data-testid="create-truck-submit">
              {busy ? "Creating..." : "Create Truck & Assign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function UnassignedView({ orders, selected, totals, canAct, onToggle, onToggleAll, onCreateTruck, onOpenOrder }) {
  const allChecked = orders.length > 0 && selected.size === orders.length;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="unassigned-summary">
        <Card className="p-3.5"><div className="text-[10px] uppercase tracking-wider text-slate-500">Production Completed</div><div className="text-[22px] font-bold text-slate-900 tabular-nums leading-none mt-0.5">{orders.length}</div><div className="text-[11px] text-slate-500 mt-1">orders awaiting truck</div></Card>
        <Card className="p-3.5"><div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Package className="h-3 w-3" /> Selected</div><div className="text-[22px] font-bold text-slate-900 tabular-nums leading-none mt-0.5">{totals.count}</div></Card>
        <Card className="p-3.5"><div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Package className="h-3 w-3" /> Total Boxes (sel)</div><div className="text-[22px] font-bold text-slate-900 tabular-nums leading-none mt-0.5">{num(totals.boxes)}</div></Card>
        <Card className="p-3.5"><div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Weight className="h-3 w-3" /> Total Weight (sel)</div><div className="text-[22px] font-bold text-slate-900 tabular-nums leading-none mt-0.5">{num(totals.weight)}<span className="text-xs font-normal text-slate-500"> kg</span></div></Card>
      </div>

      {selected.size > 0 && canAct && (
        <Card className="p-3 border border-slate-900 bg-slate-900 text-white rounded-xl flex items-center justify-between flex-wrap gap-2" data-testid="unassigned-action-bar">
          <div className="text-sm">{selected.size} selected · {num(totals.boxes)} boxes · {num(totals.weight)} kg</div>
          <Button onClick={onCreateTruck} className="bg-white text-slate-900 hover:bg-slate-100" data-testid="create-truck-btn">
            <Plus className="h-4 w-4 mr-1" /> Create Truck & Assign
          </Button>
        </Card>
      )}

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="p-3 w-8"><Checkbox checked={allChecked} onCheckedChange={onToggleAll} data-testid="unassigned-toggle-all" /></th>
              <th className="p-3">PFI</th>
              <th className="p-3">Distributor</th>
              <th className="p-3">Area</th>
              <th className="p-3">Region / State</th>
              <th className="p-3 text-right">Boxes</th>
              <th className="p-3 text-right">Weight</th>
              <th className="p-3">Status</th>
              <th className="p-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-slate-500" data-testid="unassigned-empty">No Production-Completed orders waiting.</td></tr>
            )}
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`unassigned-row-${o.pfi_number}`}>
                <td className="p-3"><Checkbox checked={selected.has(o.id)} onCheckedChange={() => onToggle(o.id)} data-testid={`unassigned-check-${o.pfi_number}`} /></td>
                <td className="p-3 font-mono text-xs">{o.pfi_number}</td>
                <td className="p-3">{o.distributor_name}</td>
                <td className="p-3 text-slate-600">{o.area || "-"}</td>
                <td className="p-3 text-slate-600 text-xs">{o.region_name || o.region || "-"} · {o.state || "-"}</td>
                <td className="p-3 text-right tabular-nums">{num(o.total_dispatch_quantity || o.total_quantity)}</td>
                <td className="p-3 text-right tabular-nums">{num(o.total_weight)}</td>
                <td className="p-3"><StatusBadge status={o.status} /></td>
                <td className="p-3"><Button variant="ghost" size="sm" onClick={() => onOpenOrder(o.id)} data-testid={`unassigned-open-${o.pfi_number}`}><ChevronRight className="h-4 w-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}


function TrucksView({ trucks, canAct, onOpen, onCreate }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">{trucks.length} truck(s)</div>
        {canAct && (
          <Button onClick={onCreate} variant="outline" size="sm" data-testid="trucks-new-btn">
            <Plus className="h-4 w-4 mr-1" /> New Truck
          </Button>
        )}
      </div>
      {trucks.length === 0 ? (
        <Card className="p-8 text-center text-slate-500" data-testid="trucks-empty">
          No trucks yet. Switch to "Unassigned Orders", select PFIs and click <b>Create Truck & Assign</b>.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {trucks.map((t) => (
            <Card key={t.id} className="p-4 cursor-pointer hover:shadow-md transition" onClick={() => onOpen(t.id)} data-testid={`truck-card-${t.vehicle_number}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="font-mono font-semibold text-slate-900">{t.vehicle_number}</div>
                  <div className="text-[11px] text-slate-500">{t.truck_label}</div>
                </div>
                <TruckStatusBadge status={t.status} />
              </div>
              <div className="text-xs text-slate-600 space-y-1 mt-2">
                <div className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {t.route || "—"}</div>
                <div>Plan: {fmtDate(t.plan_date)}</div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-100">
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">PFIs</div><div className="font-bold tabular-nums text-sm">{(t.assigned_order_ids || []).length}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Boxes</div><div className="font-bold tabular-nums text-sm">{num(t.total_boxes)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Weight</div><div className="font-bold tabular-nums text-sm">{num(t.total_weight)}</div></div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
